import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSettingsSectionActive } from './SettingsShell';
import './ModelPicker.css';

/**
 * Searchable, sortable model chooser for one AI task.
 *
 * Replaces a native <select> that held hundreds of catalog models. The popup
 * is an editable combobox (search) controlling a listbox. Default and the
 * current choice are pinned above the results so neither can be lost to a
 * search or a sort, and nothing is reported to the caller until an option is
 * actually chosen -- searching and sorting are view state only.
 */

export const MODEL_SORTS = [
  { id: 'price-asc', label: 'Price low–high' },
  { id: 'price-desc', label: 'Price high–low' },
  { id: 'name-asc', label: 'Name A–Z' },
  { id: 'name-desc', label: 'Name Z–A' },
];
const DEFAULT_SORT = 'price-asc';
const PAGE_STEP = 8;

/** A published per-million price, or null. Missing, blank, NaN and negative are all "not published". */
const perMillion = value => {
  if (typeof value === 'string' && value.trim() === '') return null;
  if (typeof value !== 'number' && typeof value !== 'string') return null;
  const amount = Number(value);
  return Number.isFinite(amount) && amount >= 0 ? amount : null;
};

const formatRate = value => {
  if (value === 0) return '$0';
  const fixed = value.toFixed(2);
  if (Number(fixed) === value || value >= 1) return `$${fixed}`;
  if (value < 0.0001) return '<$0.0001';
  return `$${Number(value.toPrecision(3))}`;
};

/**
 * How a model is billed, with a label that always states the unit.
 *
 * kind: 'priced' and 'free' sort by input price; 'byo' (your own provider
 * bills you) and 'unknown' have no comparable price and always sort last.
 */
export function describePrice(model) {
  if (model?.custom_provider) return { kind: 'byo', input: null, output: null, label: 'Billed by your provider' };
  const input = perMillion(model?.input_price_per_million);
  const output = perMillion(model?.output_price_per_million);
  if (input === null) {
    return {
      kind: 'unknown',
      input,
      output,
      label: output === null ? 'Price not published' : `Input price not published · ${formatRate(output)} out per 1M tokens`,
    };
  }
  if (output === null) return { kind: 'priced', input, output, label: `${formatRate(input)} in per 1M tokens · output price not published` };
  if (input === 0 && output === 0) return { kind: 'free', input, output, label: 'Free · $0 in · $0 out per 1M tokens' };
  return { kind: 'priced', input, output, label: `${formatRate(input)} in · ${formatRate(output)} out per 1M tokens` };
}

const collator = new Intl.Collator('en', { sensitivity: 'base', numeric: true });
const nameOf = model => String(model?.name || model?.id || '');
const idOf = model => String(model?.id || '');
// Code-unit order, so ties never depend on the runtime's locale data.
const compareIds = (a, b) => {
  const left = idOf(a);
  const right = idOf(b);
  if (left === right) return 0;
  return left < right ? -1 : 1;
};
const compareNames = (a, b) => collator.compare(nameOf(a), nameOf(b)) || compareIds(a, b);
const UNPRICED_RANK = { byo: 0, unknown: 1 };
const hasPrice = price => price.kind === 'priced' || price.kind === 'free';

function comparePrices(a, b, direction) {
  const aPriced = hasPrice(a.price);
  const bPriced = hasPrice(b.price);
  // Unpriced models go last in both directions: a missing price is not cheap.
  if (aPriced !== bPriced) return aPriced ? -1 : 1;
  if (!aPriced) return (UNPRICED_RANK[a.price.kind] - UNPRICED_RANK[b.price.kind]) || compareNames(a.model, b.model);
  if (a.price.input !== b.price.input) return direction * (a.price.input - b.price.input);
  const aOutput = a.price.output;
  const bOutput = b.price.output;
  if ((aOutput === null) !== (bOutput === null)) return aOutput === null ? 1 : -1;
  if (aOutput !== bOutput) return direction * (aOutput - bOutput);
  return compareNames(a.model, b.model);
}

/** Returns a new, deterministically ordered array; the input is not mutated. */
export function sortModels(models = [], sort = DEFAULT_SORT) {
  const decorated = models.map(model => ({ model, price: describePrice(model) }));
  const compare = {
    'price-asc': (a, b) => comparePrices(a, b, 1),
    'price-desc': (a, b) => comparePrices(a, b, -1),
    'name-asc': (a, b) => compareNames(a.model, b.model),
    'name-desc': (a, b) => collator.compare(nameOf(b.model), nameOf(a.model)) || compareIds(a.model, b.model),
  }[sort] || ((a, b) => comparePrices(a, b, 1));
  return decorated.sort(compare).map(entry => entry.model);
}

const providerOf = model => (model?.custom_provider ? model.provider_name : idOf(model).split('/')[0]) || '';
const searchTextOf = model => [nameOf(model), idOf(model), providerOf(model), model?.upstream_model_id]
  .filter(Boolean).join(' ').toLowerCase();

/** Case-insensitive match on name, provider and id; every word must match. */
export function filterModels(models = [], query = '') {
  const terms = String(query).toLowerCase().split(/\s+/).filter(Boolean);
  if (!terms.length) return models;
  return models.filter(model => {
    const text = searchTextOf(model);
    return terms.every(term => text.includes(term));
  });
}

const secondaryLine = model => (model?.custom_provider
  ? [model.provider_name, model.upstream_model_id].filter(Boolean).join(' · ')
  : idOf(model));

function OptionBody({ kicker, model, fallbackName, note, badges = [] }) {
  const price = model ? describePrice(model) : null;
  return (
    <>
      {kicker && <span className="model-picker-kicker">{kicker}</span>}
      <span className="model-picker-option-name">
        {model ? nameOf(model) : fallbackName}
        {badges.map(badge => <span key={badge} className="model-picker-badge">{badge}</span>)}
      </span>
      {model && secondaryLine(model) !== nameOf(model) && <span className="model-picker-option-id">{secondaryLine(model)}</span>}
      {note
        ? <span className="model-picker-option-price warn">{note}</span>
        : price && <span className={`model-picker-option-price ${price.kind}`}>{price.label}</span>}
    </>
  );
}

const UNLISTED_NOTE = 'Not in the current model list';

function ModelPicker({ id, label, labelledBy, models = [], value = '', currentModel = null, defaultModel = null, disabled = false, onChange }) {
  const sectionActive = useSettingsSectionActive();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState(DEFAULT_SORT);
  const [activeIndex, setActiveIndex] = useState(0);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const searchRef = useRef(null);
  const touchOpenRef = useRef(false);
  const focusSearchRef = useRef(false);
  const scrollActiveRef = useRef(false);

  const listboxId = `${id}-listbox`;
  const valueId = `${id}-value`;
  const optionId = index => `${id}-option-${index}`;

  const listed = value ? models.find(model => model.id === value) : null;
  const current = listed || currentModel || null;
  const defaultSource = defaultModel?.source || 'Balanced';
  const defaultName = defaultModel?.model ? nameOf(defaultModel.model) : (defaultModel?.id || 'Server default');

  const results = useMemo(
    () => (open ? sortModels(filterModels(models, query), sort) : []),
    [open, models, query, sort]
  );
  const entries = useMemo(() => {
    const pinned = [{ key: 'default', value: '', pinned: true }];
    if (value) pinned.push({ key: 'current', value, pinned: true });
    return [...pinned, ...results.map(model => ({ key: model.id, value: model.id, model }))];
  }, [results, value]);
  const pinnedCount = value ? 2 : 1;

  const close = useCallback((restoreFocus = true) => {
    setOpen(false);
    setQuery('');
    if (restoreFocus) triggerRef.current?.focus();
  }, []);

  useEffect(() => { if (!sectionActive) close(false); }, [sectionActive, close]);

  const openPicker = ({ focusSearch }) => {
    if (disabled) return;
    focusSearchRef.current = focusSearch;
    // Start on the choice in force so Enter/Escape are both no-ops.
    setActiveIndex(value ? 1 : 0);
    scrollActiveRef.current = true;
    setOpen(true);
  };

  const choose = nextValue => {
    if (nextValue !== value) onChange?.(nextValue);
    close(true);
  };

  useEffect(() => {
    if (!open) return undefined;
    if (focusSearchRef.current) searchRef.current?.focus();
    focusSearchRef.current = false;
    const onPointerDown = event => {
      if (rootRef.current && !rootRef.current.contains(event.target)) close(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open, close]);

  useEffect(() => {
    if (open && disabled) close(false);
  }, [open, disabled, close]);

  useEffect(() => {
    if (!open || !scrollActiveRef.current) return;
    scrollActiveRef.current = false;
    document.getElementById(optionId(activeIndex))?.scrollIntoView?.({ block: 'nearest' });
  });

  const moveActive = nextIndex => {
    scrollActiveRef.current = true;
    setActiveIndex(Math.max(0, Math.min(entries.length - 1, nextIndex)));
  };

  const onSearchKeyDown = event => {
    const moves = {
      ArrowDown: activeIndex + 1,
      ArrowUp: activeIndex - 1,
      PageDown: activeIndex + PAGE_STEP,
      PageUp: activeIndex - PAGE_STEP,
      Home: 0,
      End: entries.length - 1,
    };
    if (event.key in moves) {
      event.preventDefault();
      moveActive(moves[event.key]);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const entry = entries[activeIndex];
      if (entry) choose(entry.value);
    }
  };

  const onRootKeyDown = event => {
    // Handled on the root so it also works while focus is still on the
    // trigger (touch opening). The enclosing modal closes on a document-level
    // Escape, so an open popup must consume it.
    if (!open || event.key !== 'Escape') return;
    event.preventDefault();
    event.stopPropagation();
    close(true);
  };

  const onTriggerKeyDown = event => {
    if (disabled || open) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      openPicker({ focusSearch: true });
    }
  };

  const onTriggerClick = () => {
    if (open) return close(true);
    // Opening by touch keeps the on-screen keyboard down until the user taps search.
    openPicker({ focusSearch: !touchOpenRef.current });
    touchOpenRef.current = false;
  };

  const onRootBlur = event => {
    // Only a real focus move elsewhere (e.g. Tab) closes; pointer presses are
    // handled by the document listener so scrollbar drags do not close.
    if (open && event.relatedTarget && !rootRef.current?.contains(event.relatedTarget)) close(false);
  };

  const updateQuery = nextQuery => {
    scrollActiveRef.current = true;
    setQuery(nextQuery);
    const matches = filterModels(models, nextQuery).length;
    setActiveIndex(matches ? pinnedCount : 0);
  };

  const renderOption = (entry, index) => {
    const selected = entry.value === value;
    const className = `model-picker-option${index === activeIndex ? ' active' : ''}${selected ? ' selected' : ''}${entry.pinned ? ' pinned' : ''}`;
    let body;
    if (entry.key === 'default') {
      body = <OptionBody kicker={`Default · ${defaultSource}`} model={defaultModel?.model} fallbackName={defaultName} />;
    } else if (entry.key === 'current') {
      body = <OptionBody kicker="Current" model={current} fallbackName={value} note={listed ? null : UNLISTED_NOTE} />;
    } else {
      const badges = [];
      if (entry.model.custom_provider) badges.push('Your provider');
      if (defaultModel?.id && entry.model.id === defaultModel.id) badges.push(`${defaultSource} default`);
      body = <OptionBody model={entry.model} badges={badges} />;
    }
    return (
      <div
        key={`${entry.pinned ? 'pinned' : 'result'}:${entry.key}`}
        id={optionId(index)}
        role="option"
        aria-selected={selected}
        data-model-id={entry.value}
        className={className}
        // Keep focus (and the mobile keyboard) on the search box while tapping.
        onMouseDown={event => event.preventDefault()}
        onMouseEnter={() => setActiveIndex(index)}
        onClick={() => choose(entry.value)}
      >
        {body}
      </div>
    );
  };

  let triggerBody;
  if (!value) {
    triggerBody = <OptionBody kicker={`Default · ${defaultSource}`} model={defaultModel?.model} fallbackName={defaultName} />;
  } else {
    triggerBody = <OptionBody kicker="Chosen for this task" model={current} fallbackName={value} note={listed ? null : UNLISTED_NOTE} />;
  }

  return (
    <div className={`model-picker${open ? ' open' : ''}`} ref={rootRef} onBlur={onRootBlur} onKeyDown={onRootKeyDown}>
      <button
        type="button"
        id={id}
        ref={triggerRef}
        className="model-picker-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-labelledby={[labelledBy, valueId].filter(Boolean).join(' ')}
        disabled={disabled}
        onPointerDown={event => { touchOpenRef.current = event.pointerType === 'touch'; }}
        onKeyDown={onTriggerKeyDown}
        onClick={onTriggerClick}
      >
        <span className="model-picker-value" id={valueId}>{triggerBody}</span>
        <span className="model-picker-chevron" aria-hidden="true">▾</span>
      </button>

      {open && (
        <div className="model-picker-panel">
          <div className="model-picker-controls">
            <input
              ref={searchRef}
              type="text"
              role="combobox"
              className="model-picker-search"
              aria-label={`Search models for ${label}`}
              aria-expanded="true"
              aria-controls={listboxId}
              aria-autocomplete="list"
              aria-activedescendant={entries[activeIndex] ? optionId(activeIndex) : undefined}
              placeholder="Search name, provider or ID"
              autoComplete="off"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              enterKeyHint="done"
              value={query}
              onChange={event => updateQuery(event.target.value)}
              onKeyDown={onSearchKeyDown}
            />
            <select
              className="model-picker-sort"
              aria-label={`Sort models for ${label}`}
              value={sort}
              onChange={event => { scrollActiveRef.current = true; setSort(event.target.value); setActiveIndex(results.length ? pinnedCount : 0); }}
            >
              {MODEL_SORTS.map(option => <option key={option.id} value={option.id}>{option.label}</option>)}
            </select>
          </div>
          <div role="listbox" id={listboxId} aria-label={`${label} models`} className="model-picker-listbox">
            <div role="group" aria-label="Default and current choice" className="model-picker-pinned">
              {entries.slice(0, pinnedCount).map(renderOption)}
            </div>
            {results.length > 0 && (
              <div role="group" aria-label="Matching models" className="model-picker-results">
                {entries.slice(pinnedCount).map((entry, offset) => renderOption(entry, offset + pinnedCount))}
              </div>
            )}
          </div>
          {results.length === 0 && (
            <div className="model-picker-empty">
              <span>No models match “{query.trim()}”. Default and current stay available above.</span>
              <button type="button" className="model-picker-clear" onClick={() => { updateQuery(''); searchRef.current?.focus(); }}>Clear search</button>
            </div>
          )}
          <p className="model-picker-status" role="status" aria-live="polite">
            {results.length} model{results.length === 1 ? '' : 's'}
          </p>
        </div>
      )}
    </div>
  );
}

export default ModelPicker;
